<template>
  <form @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />

    <!-- if multiple sites have been passed into props.sites show the select input -->
    <FormSelect
      v-if="sites.length > 1"
      name="site"
      v-model="fields.siteId"
      test-id="site-input"
      :label="`${$t('global.site')} *`"
      :errors="validation.fields.siteId.errors"
      :dirty="validation.fields.siteId.dirty"
    >
      <option selected="true" value=""> - {{ $t("global.select-site") }} - </option>
      <option
        :value="site.id"
        v-for="site in sites"
        :key="site.id"
        :disabled="!site.isStable"
        :selected="fields.siteId === site.id"
      >
        {{ site.domain }}
      </option>
    </FormSelect>

    <div v-else class="mb-4">{{ sites[0].domain }}</div>

    <FormInput
      name="name"
      v-model="fields.name"
      testId="name-input"
      :placeholder="$t('global.name')"
      :label="`${$t('global.name')} *`"
      :errors="validation.fields.name.errors"
      :dirty="validation.fields.name.dirty"
      :disabled="isSubmitting"
    />
    <div class="form-buttons text-right mt-4">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-create-stencil-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="isSubmitting"
        :loading="isSubmitting"
        data-test="create-stencil-button"
      >
        {{ $t("global.add") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import FormSelect from "@/components/display/form/FormSelect";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { addStencil as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "StencilAddForm",
  props: {
    sites: {
      type: Array,
      validator: (value) => {
        return value.length > 0;
      },
    },
  },
  components: {
    Btn,
    FormInput,
    FormMessage,
    FormSelect,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({
      siteId: null,
      name: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        siteId: {
          required: root.$t("validation.site-required"),
        },
        name: {
          required: root.$t("validation.name-required"),
        },
      }
    );

    const cancel = () => emit("cancel");

    const submit = () => {
      errors.value = null;

      validation.validate().then(() => {
        isSubmitting.value = true;
        store
          .dispatch("stencils/create", {
            siteId: fields.siteId,
            name: fields.name,
          })
          .then(() => {
            store.dispatch("toasts/addToast", {
              variant: "success",
              message: root.$t("toasts.stencil-add-message", { name: fields.name }),
            });
            emit("success");
          })
          .catch(() => {
            store.dispatch("toasts/addToast", {
              variant: "error",
              message: root.$t("toasts.stencil-add-error-message", { name: fields.name }),
            });
          })
          .finally(() => {
            isSubmitting.value = false;
          });
      });
    };

    onMounted(() => {
      if (props.sites.length === 1) {
        fields.siteId = props.sites[0].id;
      }
    });

    return {
      errors,
      fields,
      isSubmitting,
      submit,
      validation,
      cancel,
    };
  },
};
</script>
<style lang="scss" scoped></style>
